/**
 * CurrencyProvider Component (CURR-7)
 *
 * Provides multi-currency conversion context for Core Frontend.
 * Handles conversion from multiple source currencies (ARS, BTC, ETH, etc.)
 * to the user's preferred currency.
 *
 * @example
 * ```tsx
 * <CurrencyProvider>
 *   <FinancesPage />
 * </CurrencyProvider>
 *
 * // In child component
 * const { convert, userCurrency, getExchangeRate } = useCurrency();
 * const priceInUserCurrency = convert(1452.50, 'ARS'); // e.g., 1.00 USD
 * const btcInUserCurrency = convert(0.5, 'BTC'); // e.g., 50000 USD
 * ```
 */

import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  type ExchangeRates,
  getRequiredPairs,
  convertCurrency,
  formatCurrency,
} from '@teletienda/shared-ui-lib';
import { useAuth } from '@teletienda/shared-auth-ui';
import { usePreferencesStore } from '@/stores/preferencesStore';

// ============================================================================
// Types
// ============================================================================

interface CurrencyContextValue {
  /** Convert amount from any source currency to user's preferred currency */
  convert: (amount: number, fromCurrency: string) => number | null;
  /** Current exchange rates */
  rates: ExchangeRates;
  /** User's preferred currency (from auth state or 'USD') */
  userCurrency: string;
  /** Get exchange rate for a specific source currency to user currency */
  getExchangeRate: (fromCurrency: string) => number | null;
  /** Loading state for exchange rates */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Format amount in user's currency */
  formatInUserCurrency: (amount: number) => string;
  /** Format converted price with original in parentheses */
  formatDualCurrency: (amount: number, fromCurrency: string) => string;
  /** Manually refresh rates */
  refreshRates: () => Promise<void>;
}

interface ExchangeRatesApiResponse {
  success: boolean;
  data: {
    rates: ExchangeRates;
    timestamp: string;
  };
}

// ============================================================================
// Constants
// ============================================================================

/** Stale time: 5 minutes */
const STALE_TIME = 5 * 60 * 1000;

/** Garbage collection time: 10 minutes */
const GC_TIME = 10 * 60 * 1000;

/** Common source currencies to pre-fetch for Core Frontend */
const COMMON_SOURCE_CURRENCIES = ['ARS', 'BTC', 'ETH', 'USDT', 'TRX'];

// ============================================================================
// Context
// ============================================================================

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface CurrencyProviderProps {
  children: ReactNode;
}

export function CurrencyProvider({ children }: CurrencyProviderProps) {
  // Get user's preferred currency from auth state
  const { user } = useAuth();
  // PREF-001: Use preferencesStore for anonymous users
  const { currency: anonymousCurrency } = usePreferencesStore();
  const userCurrency = (user as any)?.preferred_currency || anonymousCurrency;

  // Calculate all pairs needed for common currencies
  const allPairs = useMemo(() => {
    const pairs = new Set<string>();
    for (const currency of COMMON_SOURCE_CURRENCIES) {
      const requiredPairs = getRequiredPairs(currency, userCurrency);
      requiredPairs.forEach((pair) => pairs.add(pair));
    }
    return Array.from(pairs);
  }, [userCurrency]);

  // Get API base URL
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  // Fetch all rates at once
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['exchangeRates', 'core', allPairs.join(',')],
    queryFn: async (): Promise<{ rates: ExchangeRates; timestamp: string }> => {
      if (allPairs.length === 0) {
        return { rates: {}, timestamp: new Date().toISOString() };
      }

      const url = `${baseUrl}/api/v2/exchange-rates?pairs=${allPairs.join(',')}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch exchange rates: ${response.status}`);
      }

      const json = (await response.json()) as ExchangeRatesApiResponse;
      if (!json.success) {
        throw new Error('API returned unsuccessful response');
      }

      return json.data;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: allPairs.length > 0,
  });

  const rates = data?.rates || {};

  // Convert amount from any currency to user's preferred currency
  const convert = useCallback(
    (amount: number, fromCurrency: string): number | null => {
      if (fromCurrency === userCurrency) {
        return amount;
      }
      if (!data?.rates) {
        return null;
      }
      return convertCurrency(amount, fromCurrency, userCurrency, data.rates);
    },
    [data?.rates, userCurrency]
  );

  // Get exchange rate for a specific source currency
  const getExchangeRate = useCallback(
    (fromCurrency: string): number | null => {
      if (fromCurrency === userCurrency) {
        return 1;
      }
      if (!data?.rates) {
        return null;
      }
      // Calculate effective rate by converting 1 unit
      return convertCurrency(1, fromCurrency, userCurrency, data.rates);
    },
    [data?.rates, userCurrency]
  );

  // Format amount in user's currency
  const formatInUserCurrency = useCallback(
    (amount: number): string => {
      return formatCurrency(amount, userCurrency);
    },
    [userCurrency]
  );

  // Format with both currencies
  const formatDualCurrency = useCallback(
    (amount: number, fromCurrency: string): string => {
      const converted = convert(amount, fromCurrency);

      // Same currency or conversion not available - show single currency
      if (converted === null || fromCurrency === userCurrency) {
        return formatCurrency(amount, fromCurrency);
      }

      // Show both: converted (original)
      return `${formatCurrency(converted, userCurrency)} (${formatCurrency(amount, fromCurrency)})`;
    },
    [convert, userCurrency]
  );

  // Wrap refetch to return void
  const refreshRates = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  const contextValue = useMemo(
    (): CurrencyContextValue => ({
      convert,
      rates,
      userCurrency,
      getExchangeRate,
      isLoading,
      error: error as Error | null,
      formatInUserCurrency,
      formatDualCurrency,
      refreshRates,
    }),
    [
      convert,
      rates,
      userCurrency,
      getExchangeRate,
      isLoading,
      error,
      formatInUserCurrency,
      formatDualCurrency,
      refreshRates,
    ]
  );

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useCurrency Hook
 *
 * Access currency context for converting and formatting prices from multiple
 * source currencies to user's preferred currency.
 *
 * @throws Error if used outside CurrencyProvider
 *
 * @example
 * ```tsx
 * const { convert, getExchangeRate, userCurrency, formatDualCurrency } = useCurrency();
 *
 * // For PriceDisplay component with product in ARS
 * <PriceDisplay
 *   price={product.price}
 *   currency="ARS"
 *   displayCurrency={userCurrency}
 *   exchangeRate={getExchangeRate('ARS')}
 * />
 *
 * // For crypto balance
 * const btcInUserCurrency = convert(0.5, 'BTC');
 *
 * // For text display
 * <Text>{formatDualCurrency(balance, 'BTC')}</Text>
 * ```
 */
export function useCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
}

export default CurrencyProvider;
